import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Button, Skeleton } from 'antd';
import {
  ApartmentOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LeagueFeedList from '../LeagueFeed/LeagueFeed';
import FeedComposer from '../LeagueFeed/FeedComposer';
import TodayMatches from '../LeagueFeed/TodayMatches';
import useScrollAnchor from '../LeagueFeed/useScrollAnchor';
import { fetchHomeFeed } from '../../lib/homeFeedApi';
import { getFullTime } from '../../shared/utils/date';
import { useAppSelector } from '../../store/hooks';
import { selectHasOffer, selectUser, selectUserData } from '../../store/slices/userSlice';
import {
  ROUTE_CLUBS,
  ROUTE_COURTS,
  ROUTE_LEAGUES,
  ROUTE_MY_LEAGUE,
  ROUTE_PLAY,
  ROUTE_PLAY_MY_MATCHES_TAB,
  ROUTE_PLAY_MY_OFFERS_TAB,
  ROUTE_TOURNAMENTS,
} from '../../shared/variables/routes';
import YearReviewPromo from './YearReviewPromo';

type State =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; page: LeagueFeed.HomePage };

const SHORTCUTS = [
  { key: 'myLeague', href: ROUTE_MY_LEAGUE, icon: <TeamOutlined aria-hidden="true" /> },
  { key: 'tournaments', href: ROUTE_TOURNAMENTS, icon: <TrophyOutlined aria-hidden="true" /> },
  { key: 'courts', href: ROUTE_COURTS, icon: <EnvironmentOutlined aria-hidden="true" /> },
  { key: 'clubs', href: ROUTE_CLUBS, icon: <ApartmentOutlined aria-hidden="true" /> },
];

/**
 * Giriş yapmış oyuncunun ana sayfası (LG-54): sıradaki adım + kendi ligi, takip ettikleri ve diğer
 * liglerden haberlerin tek sütun akışı. Pazarlama bandının yerini alır; veriler istemcide yüklenir.
 */
export default function HomeDashboard() {
  const { t, i18n } = useTranslation('home');
  const user = useAppSelector(selectUser);
  const userData = useAppSelector(selectUserData);
  const hasOffer = useAppSelector(selectHasOffer);
  const viewer = userData?.uid && userData.uid === user?.uid ? userData : undefined;
  const uid = user?.uid || '';
  const scope = `${uid}:${viewer?.lig_temel_id || ''}`;
  const [stored, setState] = useState<State & { scope: string }>({ status: 'loading', scope });
  const state: State = stored.scope === scope ? stored : { status: 'loading' };
  const controller = useRef<AbortController | null>(null);
  const main = useRef<HTMLDivElement>(null);
  // Sonradan dolan bugünün maçları, yıl özeti ya da adım kartı okunan gönderiyi kaydırmaz.
  useScrollAnchor(main, '.home-dashboard__step, .home-dashboard__above-feed');

  const load = useCallback(async () => {
    if (!uid) return;
    controller.current?.abort();
    const own = new AbortController();
    controller.current = own;
    setState({ status: 'loading', scope });
    try {
      const page = await fetchHomeFeed(null, own.signal);
      if (controller.current === own) setState({ status: 'ready', page, scope });
    } catch {
      if (controller.current === own) setState({ status: 'error', scope });
    }
  }, [uid, scope]);

  useEffect(() => {
    load();
    return () => controller.current?.abort();
  }, [load]);

  const fetchPage = useCallback(
    (cursor: string | null, signal: AbortSignal) => fetchHomeFeed(cursor, signal),
    [],
  );

  // Yaklaşan maç akışla birlikte gelir; akış yüklenirken kart iskelet kalır, sonradan değişmez.
  const nextMatch = state.status === 'ready' ? state.page.nextMatch : null;
  let step: { key: string; href: string; values?: Record<string, string> } | null = null;
  if (viewer && state.status !== 'loading') {
    if (hasOffer) step = { key: 'offer', href: ROUTE_PLAY_MY_OFFERS_TAB };
    else if (nextMatch) {
      step = {
        key: nextMatch.opponent?.name ? 'match' : 'matchNoName',
        href: ROUTE_PLAY_MY_MATCHES_TAB,
        values: {
          name: nextMatch.opponent?.name || '',
          time: getFullTime(nextMatch.at / 1000, i18n.language),
          court: nextMatch.court || t('dashboard.nextStep.courtUnknown'),
        },
      };
    } else if (viewer.lig_temel_id) step = { key: 'play', href: ROUTE_PLAY };
    else step = { key: 'join', href: ROUTE_LEAGUES };
  }
  const firstName = (viewer?.ad || '').trim().split(/\s+/)[0];

  return (
    <section className="home-dashboard" aria-labelledby="home-dashboard-title">
      <div className="container home-dashboard__grid">
        <div className="home-dashboard__main" ref={main}>
          {/* Akış ilk ekranda başlasın diye selamlama ve sıradaki adım tek kompakt kutuda (3 Kasım). */}
          <div className="home-dashboard__step">
            <div className="home-dashboard__step-text">
              <h1 id="home-dashboard-title">
                {firstName
                  ? t('dashboard.greeting', { name: firstName })
                  : t('dashboard.greetingPlain')}
              </h1>
              <div aria-live="polite">
                {step ? (
                  <>
                    <h2>{t(`dashboard.nextStep.${step.key}Title`, step.values)}</h2>
                    <p>{t(`dashboard.nextStep.${step.key}Text`, step.values)}</p>
                  </>
                ) : (
                  <Skeleton active title={false} paragraph={{ rows: 1, width: '70%' }} />
                )}
              </div>
            </div>
            {step && (
              <Link href={step.href}>
                <a className="ant-btn ant-btn-primary home-dashboard__step-cta">
                  {t(`dashboard.nextStep.${step.key}Cta`)}
                </a>
              </Link>
            )}
          </div>

          <div className="home-dashboard__above-feed">
            <YearReviewPromo />
            <h2 className="visually-hidden">{t('dashboard.feedTitle')}</h2>
            <TodayMatches leagueId={viewer?.lig_temel_id} viewer={viewer} />
            {viewer && <FeedComposer key={uid} onPosted={load} />}
          </div>
          <div className="home-dashboard__feed" aria-busy={state.status === 'loading'}>
            {state.status === 'loading' && (
              <>
                <span className="visually-hidden" role="status">
                  {t('dashboard.loading')}
                </span>
                <Skeleton avatar active paragraph={{ rows: 2 }} />
                <Skeleton avatar active paragraph={{ rows: 2 }} />
                <Skeleton avatar active paragraph={{ rows: 1 }} />
              </>
            )}
            {state.status === 'error' && (
              <div className="home-league-feed__error" role="alert">
                <p>{t('dashboard.error')}</p>
                <Button onClick={load}>{t('dashboard.retry')}</Button>
              </div>
            )}
            {state.status === 'ready' && (
              <LeagueFeedList
                key={scope}
                leagueId={`home_${uid}`}
                initialEvents={state.page.events}
                initialHasMore={state.page.hasMore}
                initialNextCursor={state.page.nextCursor}
                initialContext={state.page.context}
                fetchPage={fetchPage}
                viewer={viewer}
                members={[]}
                cannotOffers={[]}
                photoStrip
                empty={
                  <>
                    <p>
                      <strong>{t('dashboard.emptyTitle')}</strong>
                    </p>
                    <p>{t('dashboard.emptyText')}</p>
                  </>
                }
              />
            )}
          </div>
        </div>

        <aside className="home-dashboard__aside" aria-labelledby="home-dashboard-shortcuts">
          <h2 id="home-dashboard-shortcuts">{t('dashboard.shortcuts.title')}</h2>
          <ul>
            {SHORTCUTS.map(item => (
              <li key={item.key}>
                <Link href={item.href}>
                  <a>
                    {item.icon}
                    <span>{t(`dashboard.shortcuts.${item.key}`)}</span>
                  </a>
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </section>
  );
}
